import { useState } from 'react';
import { ChevronDown, ChevronRight, LayoutGrid, Type, Star, ShoppingBag, Plus } from 'lucide-react';

export interface BlockDef {
  id: string;
  name: string;
  slug: string;
  category: string;
  icon?: string;
}

interface Props {
  blockDefs: BlockDef[];
  onAddBlock: (defSlug: string) => void;
  /** Number of columns in the block grid (default: 2) */
  columns?: number;
  /** Extra className for the root container */
  className?: string;
}

// ─── Category config ──────────────────────────────────────────────────────────

const CATEGORY_ORDER = ['layout', 'content', 'commerce', 'loyalty'] as const;

const CATEGORY_META: Record<string, { label: string; Icon: typeof LayoutGrid }> = {
  layout: { label: 'Layout', Icon: LayoutGrid },
  content: { label: 'Content', Icon: Type },
  commerce: { label: 'Commerce', Icon: ShoppingBag },
  loyalty: { label: 'Loyalty', Icon: Star },
};

// ─── Skeleton previews ────────────────────────────────────────────────────────
// Small wireframe illustrations that hint at each block's visual structure.

function SkeletonHero() {
  return (
    <div className="w-full h-14 rounded bg-gradient-to-br from-zinc-300/50 to-zinc-200/50 flex flex-col items-center justify-center gap-1 px-2">
      <div className="w-14 h-1.5 rounded-full bg-white/40" />
      <div className="w-20 h-1 rounded-full bg-white/20" />
      <div className="w-10 h-2.5 rounded bg-zinc-400/50 mt-0.5" />
    </div>
  );
}

function SkeletonText() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex flex-col justify-center gap-1.5 px-3">
      <div className="w-full h-1 rounded-full bg-gray-400" />
      <div className="w-4/5 h-1 rounded-full bg-gray-300" />
      <div className="w-3/5 h-1 rounded-full bg-gray-200" />
    </div>
  );
}

function SkeletonRichText() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex flex-col justify-center gap-1 px-3">
      <div className="flex gap-1 mb-1">
        <div className="w-3 h-2 rounded-sm bg-gray-400 text-[6px] font-bold flex items-center justify-center text-gray-700">B</div>
        <div className="w-3 h-2 rounded-sm bg-gray-400 text-[6px] italic flex items-center justify-center text-gray-700">I</div>
        <div className="w-3 h-2 rounded-sm bg-gray-300" />
      </div>
      <div className="w-full h-1 rounded-full bg-gray-300" />
      <div className="w-3/4 h-1 rounded-full bg-gray-200" />
    </div>
  );
}

function SkeletonImage() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex items-center justify-center">
      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.41a2.25 2.25 0 013.182 0l2.909 2.91m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5z" />
      </svg>
    </div>
  );
}

function SkeletonBanner() {
  return (
    <div className="w-full h-14 rounded bg-amber-500/20 border border-amber-500/30 flex items-center justify-center gap-2 px-3">
      <div className="w-16 h-1.5 rounded-full bg-amber-400/50" />
      <div className="w-8 h-3 rounded bg-amber-400/40" />
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex gap-1 p-1.5">
      <div className="flex-1 rounded bg-gray-300 border border-dashed border-gray-400/30" />
      <div className="flex-1 rounded bg-gray-300 border border-dashed border-gray-400/30" />
      <div className="flex-1 rounded bg-gray-300 border border-dashed border-gray-400/30" />
    </div>
  );
}

function SkeletonForm() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex flex-col justify-center gap-1 px-3">
      <div className="w-full h-2 rounded bg-gray-400/50" />
      <div className="w-full h-2 rounded bg-gray-400/50" />
      <div className="w-10 h-2.5 rounded bg-zinc-400/40 self-end" />
    </div>
  );
}

function SkeletonCardGrid() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex gap-1 p-1.5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex-1 rounded bg-gray-300 flex flex-col gap-0.5 p-1">
          <div className="w-full h-4 rounded-sm bg-gray-200" />
          <div className="w-3/4 h-0.5 rounded-full bg-gray-400/20" />
        </div>
      ))}
    </div>
  );
}

function SkeletonFeatureGrid() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex gap-1 p-1.5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-0.5 pt-1">
          <div className="w-3 h-3 rounded-full bg-zinc-400/30" />
          <div className="w-8 h-0.5 rounded-full bg-gray-200" />
        </div>
      ))}
    </div>
  );
}

function SkeletonSpacer() {
  return (
    <div className="w-full h-14 rounded bg-gray-100 flex items-center justify-center">
      <div className="w-full border-t border-dashed border-gray-400/40" />
    </div>
  );
}

function SkeletonCta() {
  return (
    <div className="w-full h-14 rounded bg-gradient-to-r from-zinc-300/30 to-zinc-200/50 flex items-center justify-center">
      <div className="w-16 h-4 rounded bg-zinc-400/40" />
    </div>
  );
}

function SkeletonHtml() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex items-center justify-center font-mono text-[9px] text-gray-400">
      {'</>'}
    </div>
  );
}

function SkeletonTestimonial() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex items-center gap-2 px-3">
      <div className="w-5 h-5 rounded-full bg-gray-300 shrink-0" />
      <div className="flex flex-col gap-0.5 flex-1">
        <div className="w-full h-1 rounded-full bg-gray-200" />
        <div className="w-2/3 h-1 rounded-full bg-gray-400/20" />
        <div className="w-10 h-0.5 rounded-full bg-gray-300/30 mt-0.5" />
      </div>
    </div>
  );
}

function SkeletonPricing() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex gap-1 p-1.5">
      {[0, 1].map((i) => (
        <div key={i} className="flex-1 rounded bg-gray-300 flex flex-col items-center gap-0.5 p-1">
          <div className="w-4 h-1.5 rounded-full bg-gray-200" />
          <div className="w-6 h-2 rounded-sm bg-zinc-400/30 text-[5px] text-center" />
        </div>
      ))}
    </div>
  );
}

function SkeletonLoyalty() {
  return (
    <div className="w-full h-14 rounded bg-gradient-to-br from-amber-500/15 to-orange-500/10 flex items-center justify-center">
      <Star size={14} className="text-amber-400/40" />
    </div>
  );
}

function SkeletonGeneric() {
  return (
    <div className="w-full h-14 rounded bg-gray-300/30 flex items-center justify-center">
      <div className="w-6 h-6 rounded border border-dashed border-gray-400/30" />
    </div>
  );
}

function SkeletonVideo() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex items-center justify-center">
      <div className="w-8 h-8 rounded-full bg-gray-400/60 flex items-center justify-center">
        <div className="w-0 h-0 border-t-[5px] border-b-[5px] border-l-[8px] border-transparent border-l-white/40 ml-0.5" />
      </div>
    </div>
  );
}

function SkeletonAccordion() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex flex-col justify-center gap-1 px-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-1">
          <div className="w-2 h-2 text-[6px] text-gray-600 shrink-0">▸</div>
          <div className={`h-1 rounded-full bg-gray-400/${40 - i * 10}`} style={{ width: `${70 - i * 10}%` }} />
        </div>
      ))}
    </div>
  );
}

function SkeletonCountdown() {
  return (
    <div className="w-full h-14 rounded bg-gradient-to-r from-rose-500/15 to-orange-500/15 flex items-center justify-center gap-2">
      {['D', 'H', 'M', 'S'].map((l) => (
        <div key={l} className="flex flex-col items-center">
          <div className="w-5 h-4 rounded bg-gray-400/50 flex items-center justify-center text-[7px] text-gray-700 font-mono">00</div>
          <div className="text-[5px] text-gray-500 mt-0.5">{l}</div>
        </div>
      ))}
    </div>
  );
}

function SkeletonDivider() {
  return (
    <div className="w-full h-14 rounded bg-gray-100 flex items-center px-3">
      <div className="w-full flex items-center gap-1">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300/40 to-transparent" />
      </div>
    </div>
  );
}

function SkeletonStats() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex gap-1 p-1.5">
      {['2.5K', '99%', '150+'].map((n) => (
        <div key={n} className="flex-1 flex flex-col items-center justify-center">
          <div className="text-[8px] font-bold text-zinc-500">{n}</div>
          <div className="w-6 h-0.5 rounded-full bg-gray-200 mt-0.5" />
        </div>
      ))}
    </div>
  );
}

function SkeletonLogoCloud() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex items-center justify-center gap-2 px-2">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="w-6 h-4 rounded bg-gray-300" />
      ))}
    </div>
  );
}

function SkeletonTeam() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex items-center justify-center gap-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex flex-col items-center gap-0.5">
          <div className="w-5 h-5 rounded-full bg-gray-300" />
          <div className="w-5 h-0.5 rounded-full bg-gray-400/20" />
        </div>
      ))}
    </div>
  );
}

function SkeletonTimeline() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex items-center px-4">
      <div className="flex flex-col gap-1.5 relative">
        <div className="absolute left-[3px] top-1 bottom-1 w-px bg-zinc-400/30" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-zinc-400/50 shrink-0 z-10" />
            <div className={`h-1 rounded-full bg-gray-400/${40 - i * 10}`} style={{ width: `${50 - i * 5}px` }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function SkeletonMap() {
  return (
    <div className="w-full h-14 rounded bg-emerald-900/20 flex items-center justify-center">
      <svg className="w-5 h-5 text-emerald-500/40" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
      </svg>
    </div>
  );
}

function SkeletonSocial() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex items-center justify-center gap-2">
      {['●', '●', '●', '●'].map((_, i) => (
        <div key={i} className="w-4 h-4 rounded-full bg-gray-300" />
      ))}
    </div>
  );
}

function SkeletonNewsletter() {
  return (
    <div className="w-full h-14 rounded bg-gradient-to-r from-zinc-200/30 to-zinc-200/30 flex flex-col items-center justify-center gap-1">
      <div className="w-14 h-1 rounded-full bg-white/30" />
      <div className="flex gap-1">
        <div className="w-14 h-2.5 rounded bg-gray-400/50" />
        <div className="w-7 h-2.5 rounded bg-zinc-400/40" />
      </div>
    </div>
  );
}

function SkeletonGallery() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 grid grid-cols-3 gap-0.5 p-1">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="rounded-sm bg-gray-300" />
      ))}
    </div>
  );
}

function SkeletonTabs() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex flex-col">
      <div className="flex gap-0.5 px-1.5 pt-1.5">
        <div className="w-8 h-2 rounded-t bg-zinc-400/40" />
        <div className="w-8 h-2 rounded-t bg-gray-300" />
        <div className="w-8 h-2 rounded-t bg-gray-300" />
      </div>
      <div className="flex-1 mx-1.5 mb-1.5 rounded-b bg-gray-400/30 border-t border-zinc-300/40" />
    </div>
  );
}

function SkeletonAlert() {
  return (
    <div className="w-full h-14 rounded bg-blue-500/15 border border-blue-500/25 flex items-center gap-2 px-3">
      <div className="w-3 h-3 rounded-full bg-blue-400/40 shrink-0 text-[6px] flex items-center justify-center text-blue-300">i</div>
      <div className="flex flex-col gap-0.5 flex-1">
        <div className="w-full h-1 rounded-full bg-blue-400/30" />
        <div className="w-2/3 h-1 rounded-full bg-blue-400/20" />
      </div>
    </div>
  );
}

function SkeletonBlockquote() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex items-center px-3">
      <div className="text-2xl text-gray-500/30 font-serif mr-2 leading-none">&ldquo;</div>
      <div className="flex flex-col gap-0.5 flex-1">
        <div className="w-full h-1 rounded-full bg-gray-200" />
        <div className="w-3/4 h-1 rounded-full bg-gray-400/20" />
        <div className="w-10 h-0.5 rounded-full bg-gray-300/30 mt-1" />
      </div>
    </div>
  );
}

function SkeletonProgressBar() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex flex-col justify-center gap-2 px-3">
      <div className="flex items-center gap-1">
        <div className="w-8 h-1 rounded-full bg-gray-200" />
        <div className="text-[6px] text-gray-500/50">75%</div>
      </div>
      <div className="w-full h-2 rounded-full bg-gray-300 overflow-hidden">
        <div className="w-3/4 h-full rounded-full bg-zinc-400/40" />
      </div>
    </div>
  );
}

function SkeletonEmbed() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex items-center justify-center border border-dashed border-gray-400/40">
      <div className="text-[8px] text-gray-500/50 font-mono">&lt;embed/&gt;</div>
    </div>
  );
}

function SkeletonButtonGroup() {
  return (
    <div className="w-full h-14 rounded bg-gray-300/30 flex items-center justify-center gap-1.5">
      <div className="w-12 h-4 rounded bg-zinc-400/40" />
      <div className="w-12 h-4 rounded border border-gray-400/40" />
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex flex-col p-1.5 gap-0.5">
      <div className="flex gap-0.5">
        <div className="flex-1 h-2 rounded-sm bg-gray-300" />
        <div className="flex-1 h-2 rounded-sm bg-gray-300" />
        <div className="flex-1 h-2 rounded-sm bg-gray-300" />
      </div>
      {[0, 1].map((i) => (
        <div key={i} className="flex gap-0.5">
          <div className="flex-1 h-2 rounded-sm bg-gray-400/30" />
          <div className="flex-1 h-2 rounded-sm bg-gray-400/30" />
          <div className="flex-1 h-2 rounded-sm bg-gray-400/30" />
        </div>
      ))}
    </div>
  );
}

function SkeletonCollection() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex flex-col gap-0.5 p-1.5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-1">
          <div className="w-4 h-3 rounded-sm bg-gray-300 shrink-0" />
          <div className={`h-1 rounded-full bg-gray-400/${40 - i * 10} flex-1`} />
        </div>
      ))}
    </div>
  );
}

function SkeletonIconBoxes() {
  return (
    <div className="w-full h-14 rounded bg-gray-200 flex gap-1 p-1.5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex-1 rounded border border-gray-400/30 flex flex-col items-center justify-center gap-0.5 p-0.5">
          <div className="w-3 h-3 rounded bg-zinc-400/25" />
          <div className="w-6 h-0.5 rounded-full bg-gray-400/25" />
        </div>
      ))}
    </div>
  );
}

const SKELETON_MAP: Record<string, () => JSX.Element> = {
  hero: SkeletonHero,
  text: SkeletonText,
  rich_text: SkeletonRichText,
  image: SkeletonImage,
  banner: SkeletonBanner,
  row: SkeletonRow,
  form: SkeletonForm,
  card_grid: SkeletonCardGrid,
  feature_grid: SkeletonFeatureGrid,
  spacer: SkeletonSpacer,
  cta: SkeletonCta,
  html: SkeletonHtml,
  testimonial: SkeletonTestimonial,
  pricing_table: SkeletonPricing,
  video: SkeletonVideo,
  accordion: SkeletonAccordion,
  countdown: SkeletonCountdown,
  divider: SkeletonDivider,
  stats: SkeletonStats,
  logo_cloud: SkeletonLogoCloud,
  team: SkeletonTeam,
  timeline: SkeletonTimeline,
  map: SkeletonMap,
  social_links: SkeletonSocial,
  newsletter: SkeletonNewsletter,
  gallery: SkeletonGallery,
  tabs: SkeletonTabs,
  alert: SkeletonAlert,
  blockquote: SkeletonBlockquote,
  progress_bar: SkeletonProgressBar,
  embed: SkeletonEmbed,
  button_group: SkeletonButtonGroup,
  data_table: SkeletonTable,
  collection: SkeletonCollection,
  icon_boxes: SkeletonIconBoxes,
};

// ─── Component ────────────────────────────────────────────────────────────────

export function BlockPalette({ blockDefs, onAddBlock, columns = 2, className }: Props) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Group by category
  const grouped = blockDefs.reduce<Record<string, BlockDef[]>>((acc, def) => {
    const cat = def.category || 'content';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(def);
    return acc;
  }, {});

  const toggle = (cat: string) => setCollapsed((c) => ({ ...c, [cat]: !c[cat] }));

  return (
    <div className={className ?? "flex w-56 flex-col overflow-y-auto border-r border-gray-200 bg-white"}>
      <div className="px-3 py-2.5 border-b border-gray-200">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-600">Blocks</span>
      </div>

      <div className="flex-1 overflow-y-auto py-1">
        {CATEGORY_ORDER.filter((cat) => grouped[cat]?.length).map((category) => {
          const defs = grouped[category] ?? [];
          const meta = CATEGORY_META[category] ?? { label: category, Icon: LayoutGrid };
          const isCollapsed = collapsed[category] ?? false;

          return (
            <div key={category}>
              <button
                onClick={() => toggle(category)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-100 transition-colors"
              >
                {isCollapsed ? (
                  <ChevronRight size={12} className="text-gray-500 shrink-0" />
                ) : (
                  <ChevronDown size={12} className="text-gray-500 shrink-0" />
                )}
                <meta.Icon size={12} className="text-gray-600 shrink-0" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 flex-1">
                  {meta.label}
                </span>
                <span className="text-[9px] text-gray-400">{defs.length}</span>
              </button>

              {!isCollapsed && (
                <div className={`grid gap-1 px-2 pb-2`} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
                  {defs.map((def) => {
                    const Skeleton = SKELETON_MAP[def.slug] ??
                      (def.slug.startsWith('loyalty_') ? SkeletonLoyalty : SkeletonGeneric);
                    return (
                      <button
                        key={def.id}
                        onClick={() => onAddBlock(def.slug)}
                        className="group flex flex-col rounded-lg border border-gray-200 bg-gray-50 overflow-hidden transition-all hover:border-zinc-400 hover:bg-zinc-100 hover:scale-[1.02] active:scale-[0.98]"
                        title={def.name}
                      >
                        <div className="p-1">
                          <Skeleton />
                        </div>
                        <div className="px-1.5 pb-1.5 pt-0.5">
                          <span className="text-[9px] font-medium text-gray-600 group-hover:text-zinc-900 truncate block leading-tight">
                            {def.name}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Uncategorized */}
        {Object.entries(grouped)
          .filter(([cat]) => !(CATEGORY_ORDER as readonly string[]).includes(cat))
          .map(([category, defs]) => (
            <div key={category}>
              <button
                onClick={() => toggle(category)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-100"
              >
                {collapsed[category] ? <ChevronRight size={12} className="text-gray-500" /> : <ChevronDown size={12} className="text-gray-500" />}
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-600">{category}</span>
              </button>
              {!collapsed[category] && (
                <div className={`grid gap-1 px-2 pb-2`} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
                  {defs.map((def) => (
                    <button
                      key={def.id}
                      onClick={() => onAddBlock(def.slug)}
                      className="group flex flex-col rounded-lg border border-gray-200 bg-gray-50 overflow-hidden transition-all hover:border-zinc-400 hover:bg-zinc-100"
                    >
                      <div className="p-1"><SkeletonGeneric /></div>
                      <div className="px-1.5 pb-1.5 pt-0.5">
                        <span className="text-[9px] font-medium text-gray-600 group-hover:text-zinc-900 truncate block">{def.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
      </div>

      {blockDefs.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <Plus size={20} className="mb-2" />
          <span className="text-[10px]">No block definitions</span>
        </div>
      )}
    </div>
  );
}
